/**
 * Admin Dashboard Page
 * 
 * Main dashboard for admin users with statistics and navigation
 */

import { useEffect, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BarChart3, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Shield,
  Activity,
  Database,
  CreditCard,
  Tags,
  UserCheck,
  Receipt,
  Brain,
  Workflow,
  UserPlus,
  Mail,
  Ticket,
  Bell,
  BookOpen
} from 'lucide-react';
import { useAdminStore, useAdminAuth } from '../stores/adminStore';
import { SidebarItem } from '../types';
import ApiStatusMonitor from '../components/ApiStatusMonitor';

const sidebarItems: SidebarItem[] = [
  {
    icon: BarChart3,
    label: 'Dashboard',
    path: '/manage/dashboard'
  },
  {
    icon: Users,
    label: 'Users',
    path: '/manage/users'
  },
  {
    icon: Database,
    label: 'Albums',
    path: '/manage/albums'
  },
  {
    icon: Receipt,
    label: 'Transactions',
    path: '/manage/transactions'
  },
  {
    icon: Brain,
    label: 'AI Analytics',
    path: '/manage/ai-analytics'
  },
  {
    icon: CreditCard,
    label: 'Credits',
    path: '/manage/credits'
  },
  {
    icon: Tags,
    label: 'Metadata',
    path: '/manage/metadata'
  },
  {
    icon: UserPlus,
    label: 'Affiliates',
    path: '/manage/affiliates'
  },
  {
    icon: Ticket,
    label: 'Voucher',
    path: '/manage/voucher'
  },
  {
    icon: Mail,
    label: 'Manage Email',
    path: '/manage/email'
  },
  {
    icon: BookOpen,
    label: 'Blog',
    path: '/manage/blog'
  },
  {
    icon: Workflow,
    label: 'Traffic Management',
    path: '/manage/traffic'
  },
  {
    icon: Activity,
    label: 'Activity',
    path: '/manage/activity'
  },
  {
    icon: Settings,
    label: 'Settings',
    path: '/manage/settings'
  },
  {
    icon: Bell,
    label: 'Push Notifications',
    path: '/manage/push-notifications'
  }
];

export default function AdminDashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, isAuthenticated } = useAdminAuth();
  const { logout, getDashboardStats, dashboardStats } = useAdminStore();
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [aiMetadataFilter, setAiMetadataFilter] = useState<'30days' | 'week' | 'today' | 'all'>('30days');
  const [aiMetadataData, setAiMetadataData] = useState<Array<{ date: string; count: number }>>([]);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

  // Get admin token
  const getAdminToken = () => {
    const adminAuth = localStorage.getItem('admin-auth');
    if (adminAuth) {
      try {
        const parsed = JSON.parse(adminAuth);
        return parsed.state?.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Fetch AI metadata data with filter
  const fetchAiMetadataData = useCallback(async (filter: string) => {
    try {
      const token = getAdminToken();
      if (!token) return;

      const response = await axios.get(
        `${API_BASE_URL}/admin/dashboard/ai-metadata?period=${filter}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setAiMetadataData(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch AI metadata data:', err);
    }
  }, [API_BASE_URL]);

  // Load dashboard stats on mount
  useEffect(() => {
    if (isAuthenticated) {
      getDashboardStats();
    }
  }, [isAuthenticated, getDashboardStats]);

  // Fetch AI metadata data when filter changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchAiMetadataData(aiMetadataFilter);
    }
  }, [isAuthenticated, aiMetadataFilter, fetchAiMetadataData]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/manage', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    try {
      logout();
      navigate('/manage', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleSidebarItemClick = (path: string) => {
    navigate(path);
    setIsSidebarOpen(false); // Close mobile sidebar
  };

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated || !admin) {
    return null;
  }

  const isCurrentPath = (path: string) => {
    return location.pathname === path || 
           (path === '/manage/dashboard' && location.pathname === '/manage/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:inset-0
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-semibold text-lg">Admin Panel</h1>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Admin Info */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">
                  {admin.username.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{admin.username}</p>
                <p className="text-gray-400 text-sm capitalize">{admin.role}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = isCurrentPath(item.path);
              
              return (
                <button
                  key={item.path}
                  onClick={() => handleSidebarItemClick(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors
                    ${isActive 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              Admin Dashboard
            </h1>
            <div></div> {/* Spacer */}
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 p-6">
          {/* Default Dashboard Content - only show on /manage/dashboard */}
          {location.pathname === '/manage/dashboard' ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                  <p className="text-gray-600 mt-1">
                    Welcome back, {admin.username}
                  </p>
                </div>
                <div className="text-sm text-gray-500">
                  Last updated: {dashboardStats?.lastUpdated ? 
                    new Date(dashboardStats.lastUpdated).toLocaleString() : 
                    'Loading...'}
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Users</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats?.totalUsers ?? '...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Database className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Albums</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats?.totalAlbums ?? '...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CreditCard className="w-8 h-8 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Assets</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats?.totalAssets ?? '...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Receipt className="w-8 h-8 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats?.totalTransactions ?? '...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserCheck className="w-8 h-8 text-teal-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Users</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardStats?.activeUsers ?? '...'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section - 2 Columns */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* AI Metadata Generation Chart */}
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Brain className="w-5 h-5 mr-2 text-blue-600" />
                      AI Metadata Generation
                    </h3>
                    <select
                      value={aiMetadataFilter}
                      onChange={(e) => setAiMetadataFilter(e.target.value as any)}
                      className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="30days">Last 30 Days</option>
                      <option value="all">All Time</option>
                    </select>
                  </div>
                  <div className="relative h-64">
                    <svg className="w-full h-full" viewBox="0 0 800 200">
                      {/* Grid lines */}
                      {[0, 1, 2, 3, 4].map((i) => (
                        <line
                          key={`grid-${i}`}
                          x1="50"
                          y1={40 + i * 40}
                          x2="780"
                          y2={40 + i * 40}
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                      ))}
                      
                      {/* Line chart */}
                      {aiMetadataData.length > 0 && (
                        <>
                          <polyline
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="2"
                            points={aiMetadataData
                              .map((item, index) => {
                                const x = 50 + (index / Math.max(aiMetadataData.length - 1, 1)) * 730;
                                const maxCount = Math.max(...aiMetadataData.map(d => d.count), 1);
                                const y = 180 - (item.count / maxCount) * 140;
                                return `${x},${y}`;
                              })
                              .join(' ')}
                          />
                          
                          {/* Data points */}
                          {aiMetadataData.map((item, index) => {
                            const x = 50 + (index / Math.max(aiMetadataData.length - 1, 1)) * 730;
                            const maxCount = Math.max(...aiMetadataData.map(d => d.count), 1);
                            const y = 180 - (item.count / maxCount) * 140;
                            return (
                              <circle
                                key={`point-${index}`}
                                cx={x}
                                cy={y}
                                r="3"
                                fill="#3b82f6"
                              >
                                <title>{`${item.date}: ${item.count} requests`}</title>
                              </circle>
                            );
                          })}
                        </>
                      )}
                      
                      {/* Y-axis labels */}
                      {[0, 1, 2, 3, 4].map((i) => {
                        const maxCount = aiMetadataData.length > 0 ? Math.max(...aiMetadataData.map(d => d.count), 1) : 100;
                        const value = Math.round((maxCount / 4) * (4 - i));
                        return (
                          <text
                            key={`y-label-${i}`}
                            x="45"
                            y={40 + i * 40 + 5}
                            textAnchor="end"
                            className="text-xs fill-gray-600"
                          >
                            {value}
                          </text>
                        );
                      })}
                      
                      {/* X-axis labels */}
                      {aiMetadataData.length > 0 && aiMetadataData
                        .filter((_, index) => {
                          const step = Math.ceil(aiMetadataData.length / 5);
                          return index % step === 0 || index === aiMetadataData.length - 1;
                        })
                        .map((item) => {
                          const originalIndex = aiMetadataData.findIndex(d => d.date === item.date);
                          const x = 50 + (originalIndex / Math.max(aiMetadataData.length - 1, 1)) * 730;
                          return (
                            <text
                              key={`x-label-${originalIndex}`}
                              x={x}
                              y="195"
                              textAnchor="middle"
                              className="text-xs fill-gray-600"
                            >
                              {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </text>
                          );
                        })}
                    </svg>
                  </div>
                  <div className="mt-4 text-sm text-gray-600 text-center">
                    Total Requests: {aiMetadataData.reduce((sum, item) => sum + item.count, 0).toLocaleString()}
                  </div>
                </div>

                {/* User Growth Chart (Dummy) */}
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-green-600" />
                    User Growth (Last 30 Days)
                  </h3>
                  <div className="relative h-64">
                    <svg className="w-full h-full" viewBox="0 0 800 200">
                      {/* Grid lines */}
                      {[0, 1, 2, 3, 4].map((i) => (
                        <line
                          key={`grid-${i}`}
                          x1="50"
                          y1={40 + i * 40}
                          x2="780"
                          y2={40 + i * 40}
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                      ))}
                      
                      {/* Dummy line chart - upward trend */}
                      <polyline
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        points="50,140 200,130 350,110 500,100 650,80 780,60"
                      />
                      
                      {/* Dummy data points */}
                      {[
                        { x: 50, y: 140 },
                        { x: 200, y: 130 },
                        { x: 350, y: 110 },
                        { x: 500, y: 100 },
                        { x: 650, y: 80 },
                        { x: 780, y: 60 }
                      ].map((point, index) => (
                        <circle
                          key={`user-point-${index}`}
                          cx={point.x}
                          cy={point.y}
                          r="3"
                          fill="#10b981"
                        >
                          <title>User growth data</title>
                        </circle>
                      ))}
                      
                      {/* Y-axis labels */}
                      {[0, 50, 100, 150, 200].map((value, i) => (
                        <text
                          key={`y-label-${i}`}
                          x="45"
                          y={180 - i * 35 + 5}
                          textAnchor="end"
                          className="text-xs fill-gray-600"
                        >
                          {value}
                        </text>
                      ))}
                      
                      {/* X-axis labels */}
                      {['Oct 3', 'Oct 9', 'Oct 15', 'Oct 21', 'Oct 27', 'Nov 2'].map((label, i) => (
                        <text
                          key={`x-label-${i}`}
                          x={50 + i * 146}
                          y="195"
                          textAnchor="middle"
                          className="text-xs fill-gray-600"
                        >
                          {label}
                        </text>
                      ))}
                    </svg>
                  </div>
                  <div className="mt-4 text-sm text-gray-600 text-center">
                    <span className="text-green-600 font-semibold">+24% </span>
                    growth this period
                  </div>
                </div>
              </div>

              {/* API Status Monitor */}
              <ApiStatusMonitor />

              {/* Coming Soon Sections */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Activity monitoring coming soon</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button 
                      onClick={() => navigate('/manage/users')}
                      className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <p className="font-medium text-gray-900">View All Users</p>
                      <p className="text-sm text-gray-600">Manage user accounts</p>
                    </button>
                    <button 
                      onClick={() => navigate('/manage/settings')}
                      className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <p className="font-medium text-gray-900">System Settings</p>
                      <p className="text-sm text-gray-600">Configure application</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Nested Routes Content - for all other routes */
            <Outlet />
          )}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}